import {
  YMAPS_KEY_MISSING_ERROR,
  MAP_DESTROY_ERROR,
} from '@/shared/lib/yandexMap/yandexMapMessages';
import type { ShallowRef } from 'vue';
import { logError, normalizeError } from '@/shared/lib/errors';
import { loadYmaps } from './loadYmaps';
import type { YMaps, YMap } from './types';

export const useYandexMap = (
  containerRef: Ref<HTMLElement | null>,
  init: (ymaps: YMaps, container: HTMLElement) => YMap | null | Promise<YMap | null>,
) => {
  const config = useRuntimeConfig();
  const apiKey = String(config.public.yandexMapsApiKey ?? '');

  const mapInstance: ShallowRef<YMap | null> = shallowRef(null);
  const isReady: Ref<boolean> = ref(false);

  const setup = async (): Promise<void> => {
    try {
      const ymaps = await loadYmaps(apiKey);
      const container = containerRef.value;
      if (!container) return;
      const map = await init(ymaps, container);
      mapInstance.value = map ? markRaw(map) : null;
      isReady.value = true;
    } catch (error) {
      logError(normalizeError(error), { action: 'yandexMap:setup' });
    }
  };

  onMounted(() => {
    if (!apiKey) {
      logError(
        {
          kind: 'unknown',
          status: 0,
          message: YMAPS_KEY_MISSING_ERROR,
          fields: {},
          cause: new Error(YMAPS_KEY_MISSING_ERROR),
        },
        { action: 'yandexMap:apiKey' },
      );
      return;
    }
    nextTick(setup);
  });

  onBeforeUnmount(() => {
    const instance = mapInstance.value;
    mapInstance.value = null;
    isReady.value = false;

    if (!instance || typeof instance.destroy !== 'function') return;

    try {
      instance.destroy();
    } catch (error) {
      logError(normalizeError(error), { action: MAP_DESTROY_ERROR });
    }
  });

  return { mapInstance, isReady };
};
